import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../api/api';

const Players = () => {
  const { t } = useTranslation();
  const [players, setPlayers] = useState([]);
  const [freeAgents, setFreeAgents] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter states
  const [searchName, setSearchName] = useState('');
  const [filterTeam, setFilterTeam] = useState('');
  const [filterPosition, setFilterPosition] = useState('');
  const [filterMinOVR, setFilterMinOVR] = useState('');
  const [filterMaxOVR, setFilterMaxOVR] = useState('');
  const [filterMinBadges, setFilterMinBadges] = useState('');
  const [sortBy, setSortBy] = useState('name'); // name, overall, badges, team

  const positions = ['PG', 'SG', 'SF', 'PF', 'C'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Single combined request instead of 3 separate calls — saves ~2/3 of bandwidth
      const res = await api.get('/players/page-data');
      setPlayers(res.data.players);
      setTeams(res.data.teams);
      setFreeAgents(res.data.freeAgents);
    } catch (err) {
      setError('Failed to load players');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredAndSortedPlayers = () => {
    let filtered = [...players];

    // Filter by name
    if (searchName.trim()) {
      const search = searchName.toLowerCase();
      filtered = filtered.filter(p => 
        `${p.firstName} ${p.lastName}`.toLowerCase().includes(search)
      );
    }

    // Filter by team
    if (filterTeam) {
      filtered = filtered.filter(p => p.teamId === parseInt(filterTeam));
    }

    // Filter by position
    if (filterPosition) {
      filtered = filtered.filter(p => p.position.includes(filterPosition));
    }

    // Filter by overall rating
    if (filterMinOVR) {
      filtered = filtered.filter(p => p.overallRating >= parseInt(filterMinOVR));
    }
    if (filterMaxOVR) {
      filtered = filtered.filter(p => p.overallRating <= parseInt(filterMaxOVR));
    }

    // Filter by badges
    if (filterMinBadges) {
      filtered = filtered.filter(p => (p.badges || 0) >= parseInt(filterMinBadges));
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
        case 'overall':
          return (b.overallRating || 0) - (a.overallRating || 0);
        case 'badges':
          return (b.badges || 0) - (a.badges || 0);
        case 'team':
          return (a.team?.name || '').localeCompare(b.team?.name || '');
        default:
          return 0;
      }
    });

    return filtered;
  };

  const clearFilters = () => {
    setSearchName('');
    setFilterTeam('');
    setFilterPosition('');
    setFilterMinOVR('');
    setFilterMaxOVR('');
    setFilterMinBadges('');
    setSortBy('name');
  };

  const get2KRatingsUrl = (player) => {
    if (!player) return null;
    const playerName = `${player.firstName}-${player.lastName}`.toLowerCase();
    return `https://www.2kratings.com/${playerName}`;
  };

  const filteredPlayers = getFilteredAndSortedPlayers();

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-xl">Loading players...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <h1 className="text-4xl font-bold">Players</h1>
        <Link
          to="/compare"
          className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-indigo-600 hover:to-purple-700 transition font-semibold text-sm"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m4 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
          {t('compare.comparePlayersCta')}
        </Link>
      </div>

      {/* Filters Section */}
      <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900 rounded-lg p-6 mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Filters</h2>
          <button
            onClick={clearFilters}
            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 underline"
          >
            Clear All Filters
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Name Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Search by Name
            </label>
            <input
              type="text"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              placeholder="Enter player name..."
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
            />
          </div>

          {/* Team Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Team
            </label>
            <select
              value={filterTeam}
              onChange={(e) => setFilterTeam(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
            >
              <option value="">All Teams</option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>
                  {team.city} {team.name}
                </option>
              ))}
            </select>
          </div>

          {/* Position Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Position
            </label>
            <select
              value={filterPosition}
              onChange={(e) => setFilterPosition(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
            >
              <option value="">All Positions</option>
              {positions.map(pos => (
                <option key={pos} value={pos}>
                  {pos}
                </option>
              ))}
            </select>
          </div>

          {/* Sort By */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Sort By
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
            >
              <option value="name">Name (A-Z)</option>
              <option value="overall">Overall Rating (High-Low)</option>
              <option value="badges">Badges (High-Low)</option>
              <option value="team">Team (A-Z)</option>
            </select>
          </div>

          {/* Overall Rating Min */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Min Overall Rating
            </label>
            <input
              type="number"
              value={filterMinOVR}
              onChange={(e) => setFilterMinOVR(e.target.value)}
              placeholder="e.g. 80"
              min="0"
              max="99"
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
            />
          </div>

          {/* Overall Rating Max */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Max Overall Rating
            </label>
            <input
              type="number"
              value={filterMaxOVR}
              onChange={(e) => setFilterMaxOVR(e.target.value)}
              placeholder="e.g. 99"
              min="0"
              max="99"
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
            />
          </div>

          {/* Min Badges */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Min Badges
            </label>
            <input
              type="number"
              value={filterMinBadges}
              onChange={(e) => setFilterMinBadges(e.target.value)}
              placeholder="e.g. 10"
              min="0"
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600"
            />
          </div>
        </div>

        {/* Results Count */}
        <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
          Showing <span className="font-semibold">{filteredPlayers.length}</span> of{' '}
          <span className="font-semibold">{players.length}</span> players
        </div>
      </div>

      {/* Players Grid */}
      {filteredPlayers.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900 rounded-lg p-8 text-center text-gray-500 dark:text-gray-400">
          No players found matching your filters.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredPlayers.map(player => (
            <div
              key={player.id}
              className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900 rounded-lg overflow-hidden hover:shadow-xl transition-shadow duration-200"
            >
              {/* Player Card Header */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold truncate">
                      {player.firstName} {player.lastName}
                    </h3>
                    <p className="text-sm opacity-90 truncate">
                      {player.team?.city} {player.team?.name}
                    </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 text-blue-800 px-3 py-1 rounded font-bold text-lg ml-2">
                    {player.overallRating}
                  </div>
                </div>
              </div>

              {/* Player Card Body */}
              <div className="p-4">
                <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Position:</span>
                    <span className="ml-2 font-semibold">{player.position}</span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Badges:</span>
                    <span className="ml-2 font-semibold text-yellow-600">
                      {player.badges || 0}
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col gap-2">
                  <Link
                    to={`/player/${player.id}`}
                    className="text-center bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition text-sm font-medium"
                  >
                    View Stats
                  </Link>
                  <a
                    href={get2KRatingsUrl(player)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-center bg-gradient-to-r from-orange-500 to-red-600 text-white px-4 py-2 rounded hover:from-orange-600 hover:to-red-700 transition text-sm font-medium flex items-center justify-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    2K Ratings
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Free Agents Section */}
      <div className="mt-12">
        <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
          <span className="bg-green-100 dark:bg-green-900/30 text-green-800 px-3 py-1 rounded-full text-lg font-semibold">
            Free Agents
          </span>
          <span className="text-gray-500 dark:text-gray-400 text-lg font-normal">
            ({freeAgents.length} available)
          </span>
        </h2>

        {freeAgents.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900 rounded-lg p-8 text-center text-gray-500 dark:text-gray-400">
            No free agents available.
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">#</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Player</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Position</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">OVR</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Badges</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"></th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {freeAgents.map((player, index) => (
                  <tr key={player.id} className="hover:bg-gray-50 dark:bg-gray-800 transition-colors">
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{index + 1}</td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-gray-900 dark:text-gray-100">
                        {player.firstName} {player.lastName}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{player.position}</td>
                    <td className="px-4 py-3">
                      <span className="inline-block bg-blue-600 text-white text-sm font-bold px-2 py-0.5 rounded">
                        {player.overallRating}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-yellow-600">
                      {player.badges || 0}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/player/${player.id}`}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 text-sm font-medium"
                      >
                        View Stats
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Players;
